import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { DocChip } from "../shipment/DocChip";
import { FilePreviewModal } from "../shipment/FilePreviewModal";
import "./Purchasing.css";

/**
 * Satınalma alternatif teklif ekleri (foto + PDF) — DocChip grid + yükleme + önizleme + silme.
 * Yükleyen + tarih metadata gösterilir. Hedef: poId.
 */
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

export function PoAttachmentPanel({ poId, quoteId, canEdit, title, compact }: { poId: string; quoteId?: string | null; canEdit: boolean; title?: string; compact?: boolean }) {
  const qc = useQueryClient();
  const listQ = trpc.purchasing.attachment.list.useQuery({ poId, quoteId: quoteId ?? null }, { enabled: !!poId, staleTime: 5_000 });
  const uploadMut = trpc.purchasing.attachment.upload.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const removeMut = trpc.purchasing.attachment.remove.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      uploadMut.mutate({ poId, quoteId: quoteId ?? null, filename: file.name, dataBase64: String(reader.result) }, { onError: (er) => setErr(er.message) });
    };
    reader.readAsDataURL(file);
  }

  const rows = listQ.data ?? [];
  const label = title ?? "Genel Ekler";

  return (
    <div>
      {err ? <div className="form-banner-err" style={{ marginBottom: 8 }}>{err}</div> : null}
      <div className="po-att-head">
        {compact ? null : <span className="po-att-title">{label} ({rows.length})</span>}
        <span className="sp" />
        {canEdit ? (
          <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" style={{ display: "none" }} onChange={onPick} />
            <button type="button" className={compact ? "btn-md ghost" : "btn-md secondary"} onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}>
              <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : compact ? "Doküman" : "Doküman Ekle (Foto / PDF)"}
            </button>
          </>
        ) : null}
      </div>
      {rows.length === 0 ? (
        compact ? null : <div className="po-att-empty">Henüz ek yok{canEdit ? " — foto veya PDF yükleyebilirsiniz" : ""}.</div>
      ) : (
        <div className="po-att-grid">
          {rows.map((a) => (
            <div key={a.id} className="po-att-item">
              <DocChip url={a.url} onOpen={setPreviewUrl} onRemove={canEdit ? () => removeMut.mutate({ id: a.id }) : undefined} busy={removeMut.isPending} />
              <span className="po-att-meta" title={a.fileName ?? undefined}>{a.uploadedBy ? `${a.uploadedBy} · ` : ""}{fmtDateTime(a.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
      <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </div>
  );
}
